/**
 * Scope controls for upline brokers — a "Mine" / "My team" toggle plus a
 * downline-broker filter, mirroring the Synrgy accounts UX.
 *
 * Self-hides for brokers with no downline (`getDownlineBrokers.hasDownline` is
 * false), so individual brokers see no extra chrome. Drives the `scope` /
 * `assignedBrokerId` params consumed by the pipeline and live-groups lists.
 */

import type {ReactNode} from 'react';
import {User, Users} from 'lucide-react';

import {Button} from '@common-web/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@common-web/components/ui/select';
import {trpc} from '@common-web/lib/trpc';
import {cn} from '@common-web/lib/utils';

export type BrokerScope = 'mine' | 'team';

const BROKER_ALL = '__all__';

interface BrokerScopeControlsProps {
  scope: BrokerScope;
  onScopeChange: (scope: BrokerScope) => void;
  assignedBrokerId: string | null;
  onAssignedBrokerChange: (id: string | null) => void;
  /** Label for the "mine" pill (e.g. "My pipeline", "My groups"). */
  mineLabel?: string;
  /** Render against a dark (navy) header instead of a light surface. */
  variant?: 'light' | 'dark';
}

export function BrokerScopeControls({
  scope,
  onScopeChange,
  assignedBrokerId,
  onAssignedBrokerChange,
  mineLabel = 'Mine',
  variant = 'light',
}: BrokerScopeControlsProps) {
  const downlineQuery = trpc.broker.getDownlineBrokers.useQuery(undefined, {
    refetchOnWindowFocus: false,
  });

  // Only upline brokers (those with a downline) get scope controls.
  if (!downlineQuery.data?.hasDownline) {
    return null;
  }

  const isDark = variant === 'dark';
  const downlineBrokers = downlineQuery.data.brokers;

  const setScope = (next: BrokerScope) => {
    if (next === scope) return;
    onScopeChange(next);
    // Leaving team view clears any selected broker so it can't filter silently.
    if (next === 'mine') {
      onAssignedBrokerChange(null);
    }
  };

  return (
    <div className='flex flex-wrap items-center gap-3'>
      <div
        className={cn(
          'inline-flex rounded-full border p-0.5',
          isDark ? 'border-white/20 bg-white/10' : 'border-gray-200 bg-gray-50',
        )}
      >
        <ScopePill
          active={scope === 'mine'}
          isDark={isDark}
          icon={<User className='h-4 w-4' />}
          label={mineLabel}
          onClick={() => setScope('mine')}
        />
        <ScopePill
          active={scope === 'team'}
          isDark={isDark}
          icon={<Users className='h-4 w-4' />}
          label='My team'
          onClick={() => setScope('team')}
        />
      </div>

      {scope === 'team' && (
        <Select
          value={assignedBrokerId ?? BROKER_ALL}
          onValueChange={(v) => onAssignedBrokerChange(v === BROKER_ALL ? null : v)}
        >
          <SelectTrigger
            className={cn(
              'w-[200px]',
              isDark && 'border-white/20 bg-white/10 text-white',
            )}
          >
            <SelectValue placeholder='All team members' />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={BROKER_ALL}>All team members</SelectItem>
            {downlineBrokers.map((b) => (
              <SelectItem key={b.id} value={b.id}>
                {b.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </div>
  );
}

function ScopePill({
  active,
  isDark,
  icon,
  label,
  onClick,
}: {
  active: boolean;
  isDark: boolean;
  icon: ReactNode;
  label: string;
  onClick: () => void;
}) {
  return (
    <Button
      type='button'
      variant='ghost'
      size='sm'
      onClick={onClick}
      className={cn(
        'rounded-full px-4 transition-colors',
        active
          ? isDark
            ? 'bg-white text-brand-navy hover:bg-white'
            : 'bg-brand-blue text-white hover:bg-brand-blue'
          : isDark
            ? 'text-white hover:bg-white/10'
            : 'text-brand-gray hover:bg-gray-100',
      )}
    >
      {icon}
      <span className='ml-1.5'>{label}</span>
    </Button>
  );
}
